/**
 * The Google Places (New) adapter — the ONE place a host app talks to Places.
 *
 * Both a browser form (via a thin lookup route) and a server-side AI tool can
 * call the functions here, so there is one set of field masks, one
 * session-token policy, and one answer to "is this configured".
 *
 * ── THE KEY ────────────────────────────────────────────────────────────────
 * Server-only, deliberately. `GOOGLE_PLACES_API_KEY` first so Places can be
 * given its own key with its own restrictions and its own billing line, falling
 * back to `GOOGLE_MAPS_API_KEY` where a host app already uses one for a admin
 * geocoder. It does NOT fall back to any `NEXT_PUBLIC_…` key — for two
 * reasons: a public, HTTP-referrer-restricted key rejects a server-to-server
 * call outright, and a key that can spend Places money has no business in a
 * client bundle. The browser never sees a key; it talks to the host's own
 * route.
 *
 * ── SESSION TOKENS ARE THE COST CONTROL ────────────────────────────────────
 * Autocomplete is billed PER REQUEST, and a request fires per keystroke. With a
 * session token, Google bills the whole type-then-pick sequence as ONE session:
 * the keystroke requests land on the "Autocomplete Session Usage" SKU (no
 * charge) and the caller pays a single Place Details Essentials event. Without
 * one — or if the session is abandoned with no details call — every keystroke
 * bills on its own at the Autocomplete Requests SKU.
 *
 * So the token is not a nicety, it is the difference between one billed event
 * per address and a dozen. The rules Google enforces, which the caller must
 * honour: the SAME token goes on every autocomplete request in a session AND on
 * the details call that ends it; a token is single-use, so the caller mints a
 * fresh one after each selection. Reusing a spent token is billed as if no
 * token were sent at all.
 *
 * @see https://developers.google.com/maps/documentation/places/web-service/place-session-tokens
 */

import { placesErrorResponseSchema } from "./places-schemas.js";
import {
  parseAddressSuggestions,
  parsePlaceDetails,
  type AddressSuggestion,
} from "./normalize.js";
import type { CanonicalAddress } from "./canonical.js";

const AUTOCOMPLETE_URL = "https://places.googleapis.com/v1/places:autocomplete";
const PLACE_DETAILS_BASE_URL = "https://places.googleapis.com/v1/places";

/**
 * Field mask for the details call. Narrow on purpose: the mask decides which
 * BILLING SKU the request lands on, and these four fields keep it on Place
 * Details Essentials. Asking for `displayName`, ratings or opening hours would
 * silently promote the call to a pricier SKU for data an address form has no
 * use for.
 */
const PLACE_DETAILS_FIELD_MASK = "id,formattedAddress,addressComponents,location";

/** Suggestions shown at once. Google returns at most 5; we never want more. */
export const MAX_ADDRESS_SUGGESTIONS = 5;

/** Below this, predictions are noise and every keystroke is a billed request. */
export const MIN_ADDRESS_QUERY_LENGTH = 3;

/**
 * The capability a UI and an AI tool both program against.
 *
 * An interface rather than two bare functions so the concrete Google adapter is
 * swappable — for a test double, and for the day a different geocoder is
 * cheaper. Nothing outside this file imports the Places wire shapes.
 */
export interface AddressLookupProvider {
  suggest(query: string, sessionToken: string): Promise<AddressSuggestion[]>;
  details(placeId: string, sessionToken: string): Promise<CanonicalAddress | null>;
}

/** Minimal `fetch` shape, injected so tests need no network and no globals. */
export type FetchLike = (
  input: string,
  init?: {
    method?: string;
    headers?: Record<string, string>;
    body?: string;
    signal?: AbortSignal;
  }
) => Promise<{
  ok: boolean;
  status: number;
  json: () => Promise<unknown>;
}>;

/** Resolve the server-side Places key. `null` when the feature is unconfigured. */
export function resolvePlacesApiKey(
  env: Record<string, string | undefined> = process.env
): string | null {
  const key = env.GOOGLE_PLACES_API_KEY || env.GOOGLE_MAPS_API_KEY;
  return key && key.trim().length > 0 ? key.trim() : null;
}

/**
 * Is address lookup usable right now?
 *
 * Every surface should check this BEFORE offering the control. An
 * unconfigured deployment hides address autocomplete entirely rather than
 * showing a box that returns nothing — a visible control that cannot work is
 * worse than no control, because the user cannot tell it from a network
 * problem.
 */
export function isAddressLookupConfigured(
  env: Record<string, string | undefined> = process.env
): boolean {
  return resolvePlacesApiKey(env) !== null;
}

/**
 * Thrown ONLY inside this module, and meant to be converted at the host's own
 * boundary (a user-facing error mapper, or a `{ error }` tool result). It
 * carries the provider's own sentence, which is the difference between
 * "Places lookup failed" and "Places API has not been used in project 123
 * before".
 */
export class AddressProviderError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "AddressProviderError";
    this.status = status;
  }
}

/** Pull Google's own message off an error body, falling back to the status. */
async function describeFailure(
  response: { status: number; json: () => Promise<unknown> }
): Promise<AddressProviderError> {
  let detail = "";
  try {
    const parsed = placesErrorResponseSchema.safeParse(await response.json());
    if (parsed.success) {
      detail = parsed.data.error.message?.trim() ?? "";
    }
  } catch {
    // A non-JSON error body is itself uninformative; the status carries the
    // meaning. Deliberately swallowed — see the fallback message below.
  }
  return new AddressProviderError(
    detail || `Places request failed with status ${response.status}.`,
    response.status
  );
}

/**
 * Build the Google-backed provider.
 *
 * `fetchImpl` is injected rather than closed over so a test can assert the
 * request — specifically that the SAME session token reached both calls, which
 * is the invariant the entire cost model rests on.
 */
export function createGooglePlacesProvider(options: {
  apiKey: string;
  fetchImpl?: FetchLike;
}): AddressLookupProvider {
  const { apiKey } = options;
  const doFetch: FetchLike =
    options.fetchImpl ?? ((input, init) => fetch(input, init) as ReturnType<FetchLike>);

  return {
    async suggest(query: string, sessionToken: string): Promise<AddressSuggestion[]> {
      const trimmed = query.trim();
      if (trimmed.length < MIN_ADDRESS_QUERY_LENGTH) return [];

      const response = await doFetch(AUTOCOMPLETE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": apiKey,
        },
        body: JSON.stringify({
          input: trimmed,
          sessionToken,
          // Street addresses only. Without this the list fills with businesses
          // and localities, which resolve to a place but not to a mailable
          // street line.
          includedPrimaryTypes: ["street_address", "premise", "subpremise"],
        }),
      });

      if (!response.ok) throw await describeFailure(response);
      return parseAddressSuggestions(await response.json()).slice(
        0,
        MAX_ADDRESS_SUGGESTIONS
      );
    },

    async details(
      placeId: string,
      sessionToken: string
    ): Promise<CanonicalAddress | null> {
      const url =
        `${PLACE_DETAILS_BASE_URL}/${encodeURIComponent(placeId)}` +
        `?sessionToken=${encodeURIComponent(sessionToken)}`;

      const response = await doFetch(url, {
        method: "GET",
        headers: {
          "X-Goog-Api-Key": apiKey,
          "X-Goog-FieldMask": PLACE_DETAILS_FIELD_MASK,
        },
      });

      if (!response.ok) throw await describeFailure(response);
      return parsePlaceDetails(await response.json());
    },
  };
}

/**
 * The provider for the current environment, or `null` when unconfigured.
 *
 * Built per call, never at module scope — a credentialed client constructed at
 * import time can run during a framework's build-time "collecting page data"
 * pass with the BUILD env, not the runtime env, and die there.
 */
export function getAddressLookupProvider(
  env: Record<string, string | undefined> = process.env
): AddressLookupProvider | null {
  const apiKey = resolvePlacesApiKey(env);
  if (!apiKey) return null;
  return createGooglePlacesProvider({ apiKey });
}
